<template>
  <div class="container-fluid py-4">
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
      <div>
        <h4 class="mb-0">{{ $t("refIntegrityAdmin.title") }}</h4>
        <p class="text-muted small mb-0">{{ $t("refIntegrityAdmin.subtitle") }}</p>
      </div>
      <div class="d-flex gap-2 align-items-center flex-wrap">
        <div class="form-check form-check-inline small mb-0 me-2">
          <input
            id="refint-show-all"
            v-model="showAllTargets"
            class="form-check-input"
            type="checkbox"
          />
          <label class="form-check-label" for="refint-show-all">{{
            $t("refIntegrityAdmin.showAllTargets")
          }}</label>
        </div>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="loading"
          @click="runScan"
        >
          <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }" />
          {{ $t("refIntegrityAdmin.scan") }}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="loading || fixing || !hasIssues"
          @click="confirmFix"
        >
          <i class="fas fa-wrench me-1" />
          {{ $t("refIntegrityAdmin.fix") }}
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <div v-if="scanPayload && !loading" class="card mb-3">
      <div class="card-body py-3">
        <span class="text-muted">{{ $t("refIntegrityAdmin.totalDocsWithIssue") }}:</span>
        <strong class="ms-1">{{ scanPayload.totalDocumentsWithAnyIssue }}</strong>
        <span v-if="scanPayload.scanScope" class="text-muted small ms-2">
          · {{ scanPayload.scanScope.modelDefinitionsLoaded }} {{ $t("refIntegrityAdmin.modelDefs") }} ·
          {{ scanPayload.scanScope.targetsBuilt }} {{ $t("refIntegrityAdmin.targets") }}
        </span>
      </div>
    </div>

    <div
      v-for="block in displayedBlocks"
      :key="
        (block.kind || 'collection') +
        '-' +
        block.collectionName +
        '-' +
        (block.arrayField || '') +
        '-' +
        (block.subField || '')
      "
      class="card mb-3"
    >
      <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span>
          <code>{{ block.collectionName }}</code>
          <span v-if="block.kind === 'array-elem'" class="badge bg-info text-dark ms-2">{{
            $t("refIntegrityAdmin.kindArray")
          }}</span>
          <span v-else class="badge bg-secondary ms-2">{{ $t("refIntegrityAdmin.kindCollection") }}</span>
          <span v-if="!block.exists" class="badge bg-secondary ms-2">{{
            $t("refIntegrityAdmin.missingCollection")
          }}</span>
        </span>
        <span v-if="block.exists" class="text-muted small"
          >{{ $t("refIntegrityAdmin.docsAffected") }}: {{ block.documentsWithAnyIssue }}</span
        >
      </div>
      <div
        v-if="block.kind !== 'array-elem' && block.referenceFieldsChecked && block.referenceFieldsChecked.length"
        class="card-body py-2 border-bottom bg-light"
      >
        <div class="small text-muted mb-1">{{ $t("refIntegrityAdmin.fieldsChecked") }}</div>
        <div class="mb-1">
          <code v-for="k in block.referenceFieldsChecked" :key="block.collectionName + k" class="me-2">{{
            k
          }}</code>
        </div>
        <div class="small d-flex flex-wrap align-items-center gap-2">
          <span
            v-if="block.definitionSource === 'modeldefinition'"
            class="badge rounded-pill bg-secondary"
            >ModelDefinition</span
          >
          <span v-if="block.parentModelName && block.groupFieldKey" class="text-muted"
            ><code>{{ block.parentModelName }}</code> → <code>{{ block.groupFieldKey }}</code></span
          >
          <span v-else-if="block.parentModelName" class="text-muted"
            ><code>{{ block.parentModelName }}</code></span
          >
        </div>
      </div>
      <div
        v-if="block.kind === 'array-elem' && block.exists"
        class="card-body py-2 border-bottom bg-light"
      >
        <div class="small text-muted mb-1">{{ $t("refIntegrityAdmin.embeddedPath") }}</div>
        <div>
          <code>{{ block.arrayField }}</code> [] → <code>{{ block.subField }}</code>
        </div>
        <div v-if="block.parentModelName" class="small text-muted mt-1">
          <code>{{ block.parentModelName }}</code>
        </div>
      </div>
      <div v-if="block.exists && block.documentsWithAnyIssue === 0" class="card-body text-muted">
        {{ $t("refIntegrityAdmin.noIssues") }}
      </div>
      <div
        v-if="block.kind !== 'array-elem' && block.exists && block.fields && block.fields.length > 0"
        class="table-responsive mb-0"
      >
        <table class="table table-sm table-striped mb-0">
          <thead>
            <tr>
              <th>{{ $t("refIntegrityAdmin.field") }}</th>
              <th>{{ $t("refIntegrityAdmin.count") }}</th>
              <th>{{ $t("refIntegrityAdmin.samples") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in block.fields" :key="block.collectionName + row.field">
              <td><code>{{ row.field }}</code></td>
              <td>{{ row.count }}</td>
              <td>
                <span v-for="(s, idx) in row.samples" :key="String(s._id)" class="me-2">
                  <code class="small">{{ String(s._id) }}</code>
                  <span class="text-muted">→</span>
                  <code class="small">{{ s.value }}</code>
                  <span v-if="idx < row.samples.length - 1">; </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="block.kind === 'array-elem' && block.exists && block.documentsWithAnyIssue > 0" class="card-body">
        <div class="small text-muted mb-1">{{ $t("refIntegrityAdmin.sampleDocIds") }}</div>
        <code v-for="id in block.sampleIds" :key="String(id)" class="me-2 small">{{ String(id) }}</code>
      </div>
    </div>

    <div
      v-if="scanPayload && !loading && displayedBlocks.length === 0"
      class="alert alert-light border"
      role="status"
    >
      {{ $t("refIntegrityAdmin.filteredEmpty") }}
    </div>

    <ContentSkeleton
      v-if="loading && !scanPayload"
      variant="cards"
      :rows="3"
      :label="$t('refIntegrityAdmin.scan')"
    />
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "RefIntegrityAdmin",
  components: { ContentSkeleton },
  data() {
    return {
      loading: false,
      fixing: false,
      scanPayload: null,
      errorMessage: "",
      showAllTargets: false,
    };
  },
  computed: {
    resultBlocks() {
      return (this.scanPayload && this.scanPayload.results) || [];
    },
    displayedBlocks() {
      const blocks = this.resultBlocks;
      if (this.showAllTargets) return blocks;
      return blocks.filter(
        (b) => !b.exists || (b.documentsWithAnyIssue || 0) > 0
      );
    },
    hasIssues() {
      return (this.scanPayload && this.scanPayload.totalDocumentsWithAnyIssue) > 0;
    },
  },
  created() {
    this.runScan();
  },
  methods: {
    formatFixSummaryHtml(summary) {
      if (!Array.isArray(summary) || summary.length === 0) {
        return this.$t("refIntegrityAdmin.fixSummaryEmpty");
      }
      const esc = (t) =>
        String(t)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const parts = summary.map((s) => {
        if (s.skipped) {
          return `<p class="mb-1"><code>${esc(s.collectionName)}</code> — ${this.$t(
            "refIntegrityAdmin.skipped"
          )}</p>`;
        }
        if (s.kind === "array-elem") {
          return `<p class="mb-1"><code>${esc(s.collectionName)}</code> ${esc(
            s.arrayField || ""
          )}[].${esc(s.subField || "")} — ${this.$t("refIntegrityAdmin.docsUpdated")}:
          <strong>${s.documentsUpdated || s.modifiedTotal || 0}</strong></p>`;
        }
        const byField = (s.fields || [])
          .map((f) => `${esc(f.field)}: ${f.modified}`)
          .join("; ");
        return `<p class="mb-1"><code>${esc(s.collectionName)}</code> — ${this.$t(
          "refIntegrityAdmin.modified"
        )}: <strong>${s.modifiedTotal || 0}</strong>${byField ? ` (${byField})` : ""}</p>`;
      });
      return `<div class="text-start small">${parts.join("")}</div>`;
    },
    async runScan() {
      this.errorMessage = "";
      this.loading = true;
      try {
        const { data } = await wencesApi.get("/api/gotrading/admin/ref-integrity/scan");
        if (data && data.success) {
          this.scanPayload = data;
        } else {
          this.errorMessage = (data && data.error) || this.$t("refIntegrityAdmin.error");
        }
      } catch (e) {
        this.errorMessage =
          (e.response && e.response.data && e.response.data.error) ||
          e.message ||
          this.$t("refIntegrityAdmin.error");
      } finally {
        this.loading = false;
      }
    },
    async confirmFix() {
      if (!this.hasIssues) return;
      const r = await Swal.fire({
        title: this.$tn("refIntegrityAdmin.fixConfirmTitle"),
        text: this.$tn("refIntegrityAdmin.fixConfirmText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$tn("refIntegrityAdmin.fix"),
        cancelButtonText: this.$tn("refIntegrityAdmin.cancel"),
        focusCancel: true,
      });
      if (!r.isConfirmed) return;
      this.doFix();
    },
    async doFix() {
      this.errorMessage = "";
      this.fixing = true;
      try {
        const { data } = await wencesApi.post("/api/gotrading/admin/ref-integrity/fix");
        if (data && data.success) {
          await Swal.fire({
            icon: "success",
            title: this.$tn("refIntegrityAdmin.fixDone"),
            html: this.formatFixSummaryHtml(data.summary),
            confirmButtonText: this.$tn("refIntegrityAdmin.ok"),
          });
          await this.runScan();
        } else {
          this.errorMessage = (data && data.error) || this.$t("refIntegrityAdmin.error");
        }
      } catch (e) {
        this.errorMessage =
          (e.response && e.response.data && e.response.data.error) ||
          e.message ||
          this.$t("refIntegrityAdmin.error");
      } finally {
        this.fixing = false;
      }
    },
  },
};
</script>
